(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class AfCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-af-slide]')];
      this.dots = [...this.querySelectorAll('[data-af-slide-to]')];
      this.previousButton = this.querySelector('[data-af-previous]');
      this.nextButton = this.querySelector('[data-af-next]');
      this.track = this.querySelector('[data-af-carousel-track]');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.pointerStart = null;

      if (this.slides.length < 2) {
        this.querySelector('[data-af-carousel-controls]')?.setAttribute('hidden', '');
        this.update();
        return;
      }

      this.previousButton?.addEventListener('click', () => this.show(this.index - 1, true));
      this.nextButton?.addEventListener('click', () => this.show(this.index + 1, true));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.show(Number(dot.dataset.afSlideTo), true));
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.index - 1, true);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.index + 1, true);
        }
      });

      this.track?.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
        this.track.setPointerCapture?.(event.pointerId);
      });

      this.track?.addEventListener('pointerup', (event) => {
        if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;
        const deltaX = event.clientX - this.pointerStart.x;
        const deltaY = event.clientY - this.pointerStart.y;
        this.pointerStart = null;
        if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY)) return;
        this.show(this.index + (deltaX < 0 ? 1 : -1), true);
      });

      this.track?.addEventListener('pointercancel', () => {
        this.pointerStart = null;
      });

      this.update();
    }

    show(nextIndex, announce) {
      const count = this.slides.length;
      this.index = (nextIndex + count) % count;
      this.update();
      if (announce) {
        const title = this.slides[this.index]?.dataset.afSlideTitle;
        const status = this.querySelector('[data-af-carousel-status]');
        if (status && title) status.textContent = title;
      }
    }

    update() {
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          if (active) {
            if (element.dataset.afPreviousTabindex !== undefined) {
              const previous = element.dataset.afPreviousTabindex;
              if (previous === '') element.removeAttribute('tabindex');
              else element.setAttribute('tabindex', previous);
              delete element.dataset.afPreviousTabindex;
            }
          } else if (element.dataset.afPreviousTabindex === undefined) {
            element.dataset.afPreviousTabindex = element.getAttribute('tabindex') || '';
            element.setAttribute('tabindex', '-1');
          }
        });
      });

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === this.index;
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }
  }

  class AfMarquee extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-af-marquee-toggle]');
      this.manualPaused = false;
      this.temporaryPaused = false;

      this.toggle?.addEventListener('click', () => {
        this.manualPaused = !this.manualPaused;
        this.render();
      });
      this.addEventListener('pointerenter', () => {
        this.temporaryPaused = true;
        this.render();
      });
      this.addEventListener('pointerleave', () => {
        this.temporaryPaused = false;
        this.render();
      });
      this.addEventListener('focusin', () => {
        this.temporaryPaused = true;
        this.render();
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.temporaryPaused = false;
          this.render();
        }
      });
      reducedMotion.addEventListener?.('change', () => this.render());
      this.render();
    }

    render() {
      const motionOff = reducedMotion.matches;
      const paused = motionOff || this.manualPaused || this.temporaryPaused;
      this.classList.toggle('is-paused', paused);
      if (!this.toggle) return;
      this.toggle.disabled = motionOff;
      this.toggle.setAttribute('aria-pressed', this.manualPaused ? 'true' : 'false');
      this.toggle.textContent = motionOff
        ? this.toggle.dataset.motionOffLabel || 'Motion off'
        : this.manualPaused
          ? this.toggle.dataset.playLabel || 'Play'
          : this.toggle.dataset.pauseLabel || 'Pause';
    }
  }

  class AfRelatedProducts extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      fetch(url, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Related products request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const incoming = documentFragment.querySelector('.af-related-results');
          const target = this.querySelector('.af-related-results');
          if (!incoming || !target || !incoming.children.length) return;
          const currentProductId = this.dataset.currentProductId || '';
          const currentArtworkId = (this.dataset.artworkId || '').trim().toLowerCase();
          const currentBaseTitle = (this.dataset.baseTitle || '').trim().toLowerCase();
          const limit = Math.max(1, Number(this.dataset.limit) || 4);
          const matches = [...incoming.children]
            .filter((card) => {
              if ((card.dataset.productId || '') === currentProductId) return false;
              const candidateArtworkId = (card.dataset.artworkId || '').trim().toLowerCase();
              const candidateBaseTitle = (card.dataset.baseTitle || '').trim().toLowerCase();
              if (currentArtworkId && candidateArtworkId) return currentArtworkId === candidateArtworkId;
              return Boolean(currentBaseTitle && candidateBaseTitle && currentBaseTitle === candidateBaseTitle);
            })
            .slice(0, limit)
            .map((card) => card.cloneNode(true));
          if (!matches.length) return;
          target.replaceChildren(...matches);
          this.closest('.af-related')?.removeAttribute('hidden');
          window.AFArt?.refreshMotion?.(this);
        })
        .catch((error) => {
          if (window.Shopify?.designMode) console.warn(error);
        });
    }
  }

  if (!customElements.get('af-carousel')) customElements.define('af-carousel', AfCarousel);
  if (!customElements.get('af-marquee')) customElements.define('af-marquee', AfMarquee);
  if (!customElements.get('af-related-products')) customElements.define('af-related-products', AfRelatedProducts);

  function initializeMenus(scope = document) {
    scope.querySelectorAll('details.af-mobile-menu').forEach((details) => {
      if (details.dataset.afReady === 'true') return;
      details.dataset.afReady = 'true';
      const summary = details.querySelector('summary');
      const setState = () => summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      details.addEventListener('toggle', setState);
      details.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && details.open) {
          details.open = false;
          summary?.focus();
        }
      });
      details.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => { details.open = false; }));
      setState();
    });
  }

  function initializeVariantPickers(scope = document) {
    scope.querySelectorAll('[data-af-variant-select]').forEach((select) => {
      if (select.dataset.afReady === 'true') return;
      select.dataset.afReady = 'true';
      const form = select.closest('form');
      const button = form?.querySelector('[data-af-add-to-cart]');
      const buttonLabel = button?.querySelector('[data-af-add-label]');
      const price = form?.closest('[data-af-product-info]')?.querySelector('[data-af-product-price]');
      const update = () => {
        const option = select.selectedOptions[0];
        const available = option?.dataset.available !== 'false';
        if (button) {
          button.disabled = !available;
          button.toggleAttribute('aria-disabled', !available);
          if (buttonLabel) {
            buttonLabel.textContent = available
              ? button.dataset.addLabel || 'Add to cart'
              : button.dataset.soldLabel || 'Sold out';
          }
        }
        if (price && option?.dataset.price) price.textContent = option.dataset.price;
      };
      select.addEventListener('change', update);
      update();
    });
  }

  function initializeProductGallery(scope = document) {
    scope.querySelectorAll('[data-af-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.afReady === 'true') return;
      gallery.dataset.afReady = 'true';
      const mainImage = gallery.querySelector('[data-af-product-main-image]');
      gallery.querySelectorAll('[data-af-product-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (!mainImage || !thumb.dataset.src) return;
          mainImage.src = thumb.dataset.src;
          mainImage.srcset = '';
          if (thumb.dataset.alt) mainImage.alt = thumb.dataset.alt;
          gallery.querySelectorAll('[data-af-product-thumb]').forEach((item) => item.setAttribute('aria-current', 'false'));
          thumb.setAttribute('aria-current', 'true');
        });
      });
    });
  }

  function initializeSort(scope = document) {
    scope.querySelectorAll('[data-af-sort]').forEach((select) => {
      if (select.dataset.afReady === 'true') return;
      select.dataset.afReady = 'true';
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        if (select.value) url.searchParams.set('sort_by', select.value);
        else url.searchParams.delete('sort_by');
        window.location.assign(url.toString());
      });
    });
  }

  function initializeFallbackReveals(scope = document) {
    if (reducedMotion.matches || window.gsap) return;
    const items = [...scope.querySelectorAll('.af-reveal:not(.is-visible)')];
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.forEach((item) => observer.observe(item));
  }

  function initializeGsap(scope = document) {
    if (reducedMotion.matches || !window.gsap || !window.ScrollTrigger) {
      scope.querySelectorAll('.af-reveal').forEach((item) => item.classList.add('is-visible'));
      return;
    }

    window.gsap.registerPlugin(window.ScrollTrigger);
    let settleEase = 'power2.out';
    if (window.CustomEase) {
      window.gsap.registerPlugin(window.CustomEase);
      if (!window.AFArtSettleEaseReady) {
        window.CustomEase.create('afSettle', '0.22,0.61,0.36,1');
        window.AFArtSettleEaseReady = true;
      }
      settleEase = 'afSettle';
    }
    document.body.classList.add('af-motion-ready');

    scope.querySelectorAll('[data-af-reveal-group]').forEach((group) => {
      const pending = [...group.querySelectorAll('.af-reveal:not([data-af-gsap-ready])')];
      pending.forEach((item) => { item.dataset.afGsapReady = 'true'; });
      for (let start = 0; start < pending.length; start += 6) {
        const batch = pending.slice(start, start + 6);
        if (!batch.length) continue;
        window.gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: .76,
          stagger: .075,
          ease: settleEase,
          scrollTrigger: {
            trigger: batch[0],
            start: 'top 88%',
            once: true,
          },
          onComplete: () => batch.forEach((item) => item.classList.add('is-visible')),
        });
      }
    });

    scope.querySelectorAll('.af-reveal:not([data-af-gsap-ready])').forEach((item) => {
      item.dataset.afGsapReady = 'true';
      window.gsap.to(item, {
        opacity: 1,
        y: 0,
        duration: .76,
        ease: settleEase,
        scrollTrigger: { trigger: item, start: 'top 88%', once: true },
        onComplete: () => item.classList.add('is-visible'),
      });
    });

    scope.querySelectorAll('[data-af-parallax]:not([data-af-parallax-ready])').forEach((item) => {
      item.dataset.afParallaxReady = 'true';
      window.gsap.fromTo(item, { yPercent: -3 }, {
        yPercent: 3,
        ease: 'none',
        scrollTrigger: { trigger: item, start: 'top bottom', end: 'bottom top', scrub: .6 },
      });
    });

    window.gsap.matchMedia().add('(min-width: 990px)', () => {
      scope.querySelectorAll('[data-af-pin]:not([data-af-pin-ready])').forEach((item) => {
        item.dataset.afPinReady = 'true';
        window.ScrollTrigger.create({
          trigger: item.parentElement || item,
          start: 'top 96px',
          end: '+=28%',
          pin: item,
          pinSpacing: false,
        });
      });
    });
  }

  function initialize(scope = document) {
    initializeMenus(scope);
    initializeVariantPickers(scope);
    initializeProductGallery(scope);
    initializeSort(scope);
    initializeGsap(scope);
    initializeFallbackReveals(scope);
  }

  window.AFArt = {
    refreshMotion: initializeGsap,
    initialize,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
